import React from 'react';
import ReactDOM from 'react-dom/client';
import { RouterProvider } from 'react-router';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { queryClient } from './lib/queryClient.js';
import { router } from './router/index.js';
import { Toaster } from './components/ui/toast.js';
import { initSentry, Sentry } from './lib/sentry.js';
import { initTheme } from './lib/theme.js';
import { useAuthStore } from './store/authStore.js';
import './index.css';

initTheme();
initSentry();

// Rehydrate the access token via the refresh cookie before the router mounts.
// Fire-and-forget: ProtectedRoute watches isBootstrapping and shows a skeleton
// until this resolves.
void useAuthStore.getState().bootstrap();

function App() {
  return <RouterProvider router={router} />;
}

const SentryProfiledApp = Sentry.withProfiler(App);

const rootElement = document.getElementById('root');
if (!rootElement) throw new Error('Root element not found');

ReactDOM.createRoot(rootElement, {
  onCaughtError: (error) => {
    Sentry.captureException(error);
    if (!import.meta.env.PROD) {
      // eslint-disable-next-line no-console
      console.error('Caught error:', error);
    }
  },
  onUncaughtError: (error) => {
    Sentry.captureException(error);
    if (!import.meta.env.PROD) {
      // eslint-disable-next-line no-console
      console.error('Uncaught error:', error);
    }
  },
}).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <SentryProfiledApp />
      <Toaster />
      {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  </React.StrictMode>,
);
